import AnalysisControlBase from './AnalysisControlBase';

export default class BufferAnalysisControl extends AnalysisControlBase {
    _getPanelTitle() {
        return this.resourcesHandler.get('BufferAnalysis_PanelTitle');
    }

    _getFormBody() {
        const r = this.resourcesHandler;
        return `${this._section('AnalysisSection_Sources')}
                <div class="form-group col-12 mb-1 d-flex">
                    <div class="col-6 mb-3">
                        <div class="form-group">
                            <label class="form-label">${r.get('BufferAnalysis_Source')}:</label>
                            <input class="form-control" id="${this._id('buffer-source')}">
                        </div>
                    </div>
                    <div class="col-6 mb-3">
                        <div class="form-group">
                            <label class="form-label">${r.get('BufferAnalysis_GeometryField')}:</label>
                            <input class="form-control" id="${this._id('geometry-field')}">
                        </div>
                    </div>
                </div>
                ${this._section('AnalysisSection_Options')}
                <div class="form-group col-12 mb-1 d-flex">
                    <div class="col-6 mb-3">
                        <div class="form-group">
                            <div class="xp-analysis-field-header" style="margin-bottom:4px;">
                                <label class="form-label m-0">${r.get('BufferAnalysis_BufferSize')}:</label>
                                <label style="cursor:pointer;font-size:11px;margin:0;display:flex;align-items:center;gap:4px;">
                                    <input type="checkbox" id="${this._id('buffer-from-field')}" style="margin:0;"/>
                                    <span>${r.get('BufferAnalysis_FromField')}</span>
                                </label>
                            </div>
                            <input class="form-control" type="text" id="${this._id('buffer-size')}">
                            <input class="form-control" id="${this._id('buffer-field')}">
                        </div>
                    </div>
                    <div class="col-6 mb-3 align-content-center pt-4">
                        <div class="form-group" style="display: flex; flex-direction: row-reverse; height: 1.2rem; justify-content: flex-end;">
                            <label class="form-label ml-1">${r.get('BufferAnalysis_ExcludeOriginalGeom')}</label>
                            <div id="${this._id('exclude-original-geom')}"></div>
                        </div>
                    </div>
                </div>
                ${this._section('AnalysisSection_Output')}
                <div class="form-group col-12 mb-1 d-flex">
                    <div class="col-6 mb-3">
                        <div class="form-group">
                            <label class="form-label">${r.get('Analysis_ResultLayerName')}:</label>
                            <input class="form-control" type="text" id="${this._id('analysis-name')}">
                        </div>
                    </div>
                    <div class="col-6 mb-3 align-content-center pt-4">
                        <div class="form-group" style="display: flex; flex-direction: row-reverse; height: 1.2rem; justify-content: flex-end;">
                            <label class="form-label ml-1">${r.get('Analysis_SpatialFilter')}</label>
                            <div id="${this._id('spatial-filter')}"></div>
                        </div>
                    </div>
                </div>`;
    }

    _wireWizardEvents() {
        this._$('buffer-source').kendoComboBox(this._entityComboOptions((entity, fields) => {
            this.fields = fields;
            const geomFields = fields.filter(f => f.DataType === 'GEOMETRY' || f.DataType == null);
            this._setComboData('geometry-field', geomFields);
            const numericFields = fields.filter(f => f.DataType === 'NUMERIC' || f.DataType === 'INTEGER');
            this._setComboData('buffer-field', numericFields);
        }));

        this._$('geometry-field').kendoComboBox(this._fieldsComboOptions());
        this._$('buffer-field').kendoComboBox(this._fieldsComboOptions());
        this._$('buffer-size').kendoNumericTextBox();

        const bufferFieldWrapper = this._combo('buffer-field').wrapper;
        bufferFieldWrapper.hide();

        this._$('buffer-from-field').on('change', (evt) => {
            const useField = evt.target.checked;
            this._$('buffer-size').data('kendoNumericTextBox').wrapper.toggle(!useField);
            bufferFieldWrapper.toggle(useField);
        });

        const switchMessages = {
            checked: this.resourcesHandler.get('Yes'),
            unchecked: this.resourcesHandler.get('No')
        };

        this._$('spatial-filter').kendoSwitch({
            messages: switchMessages,
            checked: false
        });

        this._$('exclude-original-geom').kendoSwitch({
            messages: switchMessages,
            checked: false
        });
    }

    _defaultLayerName() {
        const buffer = this._selectedRow('buffer-source');
        return buffer != null ? buffer.Name + '-Buffer' : '';
    }

    _validate() {
        const errors = [
            this._vCombo('buffer-source', 'BufferAnalysis_Source'),
            this._vCombo('geometry-field', 'BufferAnalysis_GeometryField')
        ];
        if (this._$('buffer-from-field').is(':checked')) {
            errors.push(this._vCombo('buffer-field', 'BufferAnalysis_BufferSize'));
        } else {
            errors.push(this._vNumeric('buffer-size', 'BufferAnalysis_BufferSize'));
        }
        return errors;
    }

    _buildAnalysisPayload() {
        const bufferEntity = this._selectedRow('buffer-source');
        const fields = this.fields;
        const geomField = this._selectedRow('geometry-field');
        const useField = this._$('buffer-from-field').is(':checked');
        const bufferSize = this._$('buffer-size').data('kendoNumericTextBox').value();
        const bufferField = useField ? this._selectedRow('buffer-field') : null;
        const excludeOriginal = this._$('exclude-original-geom').data('kendoSwitch').check();

        if (useField && bufferField == null) {
            return null;
        }

        const sizeToken = useField ? bufferField.FieldName : bufferSize;
        const distance = useField
            ? this._fieldRef(bufferEntity, bufferField)
            : { type: 'FixedValue', dataType: 'NUMERIC', fixedValue: bufferSize };

        const data = {
            analyseType: 'Complex',
            dataSource: {},
            outputValues: [],
            layerName: this._$('analysis-name').val() === ''
                ? this._defaultLayerName()
                : this._$('analysis-name').val(),
            pkField: this._resolvePkField(bufferEntity, fields),
            geomField: geomField.FieldName + '_Buffer_' + sizeToken,
            bboxStrategy: this._$('spatial-filter').data('kendoSwitch').check()
        };

        this._applySource(data.dataSource, bufferEntity);

        fields.forEach(x => {
            if (x.Id == this._combo('geometry-field').value()) {
                if (excludeOriginal) {
                    data.outputValues.push({
                        outputName: x.FieldName + '_Buffer_' + sizeToken,
                        type: 'Expression',
                        key: 'geomDifference',
                        parameters: [
                            {
                                key: 'GEOM_1',
                                value: {
                                    type: 'Expression',
                                    key: 'buffer',
                                    parameters: [
                                        {
                                            key: 'GEOMETRY',
                                            value: this._fieldRef(bufferEntity, x),
                                        },
                                        {
                                            key: 'DISTANCE',
                                            value: distance
                                        }
                                    ]            
                                },
                            },
                            {
                                key: 'GEOM_2',
                                value: this._fieldRef(bufferEntity, x)
                            }
                        ] 
                    });
                } else {
                    data.outputValues.push({
                        outputName: x.FieldName + '_Buffer_' + sizeToken,
                        index: null,
                        type: 'Expression',
                        key: 'buffer',
                        parameters: [
                            {
                                key: 'GEOMETRY',
                                value: this._fieldRef(bufferEntity, x),
                            },
                            {
                                key: 'DISTANCE',
                                value: distance
                            }                            
                        ]                        
                    });
                }
            } else {
                const passthrough = Object.assign(
                    { outputName: x.FieldName, srid: x.Srid },
                    this._fieldRef(bufferEntity, x)
                );
                data.outputValues.push(passthrough);
            }
        });

        return data;
    }

    _resolvePkField(bufferEntity, fields) {
        if (bufferEntity.Kind === 'Analysis') {
            return bufferEntity.pkField;
        }
        const pk = fields.find(x => x.SpecialFieldType == 'PK_FIELD');
        return pk != null ? pk.FieldName : null;
    }
}
